<template>
  <CustomModal
    v-model:visible="modalVisible"
    title="Select Sender"
    width="500px"
    :source="`${source}_setup_sender_details_modal`"
    :close-on-outside-click="!isDropdownOpen"
    @close="handleClose"
  >
    <SkeletonLoader
      v-if="isInitialLoading"
      :page="loaderPages.SENDER_DETAILS_MODAL"
    />

    <div v-else class="flex flex-col gap-6">
      <div class="flex flex-col gap-4">
        <span class="text_color text-sm">
          Adding to
          <span class="text_color font-semibold text-sm"
            >'{{ sequenceDetails?.name || "" }}'</span
          >
        </span>
        <div>
          <div class="secondary_color flex items-center gap-1 text-[13px]">
            Send messages from
            <a-tooltip
              title="When multiple users or a team is selected, contacts/leads will be distributed among them in a round-robin manner."
            >
              <ThemeImage
                :icon="imageNames.INFO_GREY"
                alt="info_grey"
                class="size-4 cursor-pointer"
              />
            </a-tooltip>
          </div>
          <SearchableMultiSelectDropdown
            :relative-to-viewport="false"
            v-model="selectedMailboxes"
            :tabs="mailboxTabs"
            :tab-selection-modes="tabSelectionModes"
            :options="mailboxOptionsArray"
            placeholder="Select teams or users"
            class="w-full mt-2"
            :disabled="isLoadingMailboxData"
            :loading="isLoadingMailboxData"
            :match-trigger-width="true"
            :show-warnings="true"
            :warning-config="warningConfig"
            @selection-change="handleSelectionChange"
            @tab-change="handleTabChange"
            @dropdown-open="handleDropdownOpen"
            @dropdown-close="handleDropdownClose"
            position="start"
          />
        </div>

        <div v-if="showMailboxTable">
          <a-collapse v-model:activeKey="mailboxCollapseKey" :bordered="false" @change="handleMailboxCollapseChange">
            <a-collapse-panel
              key="mailboxes"
              :header="mailboxTableHeader"
              class="font-semibold"
            >
              <div
                class="mailbox-selector"
                v-if="!isLoadingUserMailboxes && userMailboxes.length > 0"
              >
                <CustomTable
                  :source="entityTypes.SETUP_SENDER_DETAILS"
                  :headers="mailboxTableHeaders"
                  :data="userMailboxes"
                  :loading="isLoadingUserMailboxes"
                  :show-checkboxes="true"
                  :selection="mailboxSelection"
                  :pagination="null"
                  :identifier-key="'email_account_id'"
                  :custom-column-render="customColumnRender"
                  height="300px"
                  @update:selection="handleMailboxSelection"
                />
              </div>
              <SkeletonLoader
                v-if="isLoadingUserMailboxes"
                :page="loaderPages.TABLE"
              />
              <EmptyScreen
                v-else-if="
                  !isLoadingUserMailboxes && userMailboxes.length === 0
                "
                source="select_mailbox"
                title="No mailboxes found"
                description="Please select at least one user or team"
              />
            </a-collapse-panel>
          </a-collapse>
        </div>

        <div v-if="showLinkedInTable">
          <a-collapse v-model:activeKey="linkedInCollapseKey" :bordered="false" @change="handleLinkedInCollapseChange">
            <a-collapse-panel
              key="linkedin_integrations"
              :header="linkedInTableHeader"
              class="font-semibold"
            >
              <div
                class="linkedin-selector"
                v-if="!isLoadingLinkedInIntegrations && linkedInIntegrations.length > 0"
              >
                <CustomTable
                  :source="entityTypes.SETUP_SENDER_DETAILS"
                  :headers="linkedinTableHeaders"
                  :data="linkedInIntegrations"
                  :loading="isLoadingLinkedInIntegrations"
                  :show-checkboxes="true"
                  :selection="linkedInSelection"
                  :pagination="null"
                  :identifier-key="'linkedin_integration_id'"
                  :custom-column-render="customColumnRender"
                  height="300px"
                  @update:selection="handleLinkedInSelection"
                />
              </div>
              <SkeletonLoader
                v-if="isLoadingLinkedInIntegrations"
                :page="loaderPages.TABLE"
              />
              <EmptyScreen
                v-else-if="
                  !isLoadingLinkedInIntegrations && linkedInIntegrations.length === 0
                "
                source="select_linkedin_account"
                title="No LinkedIn accounts found"
                description="Please select at least one user or team with LinkedIn accounts"
              />
            </a-collapse-panel>
          </a-collapse>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex gap-2">
        <a-button
          class="primary_medium"
          @click="handleSave"
          :disabled="isButtonDisabled"
          :loading="isSaving"
        >
          {{ ctaText }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {message} from "ant-design-vue";

import CustomModal from "@/src/components/CustomModal.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import MailboxEmailCell from "@/src/components/MailboxEmailCell.vue";
import LinkedInAccountCell from "@/src/components/LinkedInAccountCell.vue";

import SequenceService from "@/src/api/SequenceService.js";
import DashboardService from "@/src/api/DashboardService.js";

import {buildSequenceConfigData, fetchApiData,} from "@/src/utils/sequenceUtils.js";

import {imageNames} from "@/src/constants/imageConstants.js";
import {
  mailboxTabConfig,
  mailboxTableHeaders,
  linkedinTableHeaders,
  mailboxTabTypes,
  sequenceStepTypes,
  WARNING_MESSAGES,
} from "@/src/constants/sequenceConstants.js";
import {entityTypes} from "@/src/constants/entityTypeConstants";
import {loaderPages} from "@/src/constants/loaderConstants.js";
import SearchableMultiSelectDropdown from "@/src/components/SearchableMultiSelectDropdown.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import {MAILBOX_STATUS} from "@/src/constants/mailboxConstants";
import {featureNames} from "@/src/constants/globalConstants";

const props = defineProps({
  source: {
    type: String,
    required: false,
    default: "setup_sender_details",
  },
  visible: {
    type: Boolean,
    required: true,
    default: false,
  },
  sequenceId: {
    type: [String, Number],
    required: false,
    default: null,
  },
  pendingValidationData: {
    type: Object,
    required: false,
    default: null,
  },
});

const emit = defineEmits(["update:visible", "save", "cancel"]);

const modalVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const sequenceDetails = ref(null);
const isLoadingSequence = ref(false);
const selectedMailboxes = ref([]);
const currentActiveTab = ref(null);
const sequenceSteps = ref([]);
const isLoadingSteps = ref(false);

const users = ref([]);
const teams = ref([]);
const isLoadingUsers = ref(false);
const isLoadingTeams = ref(false);

const userMailboxes = ref([]);
const isLoadingUserMailboxes = ref(false);
const showMailboxTable = ref(false);

const linkedInIntegrations = ref([]);
const isLoadingLinkedInIntegrations = ref(false);
const showLinkedInTable = ref(false);
const isSaving = ref(false);

const isDropdownOpen = ref(false);

const mailboxCollapseKey = ref([]);
const linkedInCollapseKey = ref([]);

const mailboxTabs = ref(mailboxTabConfig.tabs);
const tabSelectionModes = ref(mailboxTabConfig.selectionModes);

const mailboxOptionsArray = computed(() => [users.value, teams.value]);

const isLoadingMailboxData = computed(
  () => isLoadingSequence.value || isLoadingUsers.value || isLoadingTeams.value,
);

const isInitialLoading = computed(() => {
  return (
    isLoadingSequence.value || isLoadingUsers.value || isLoadingTeams.value
  );
});

const customColumnRender = {
  email: MailboxEmailCell,
  account_name: LinkedInAccountCell,
};

const mailboxSelection = ref({
  selected_ids: [],
  unselected_ids: [],
  select_all: false,
});

const linkedInSelection = ref({
  selected_ids: [],
  unselected_ids: [],
  select_all: false,
});

const hasEmailSteps = computed(() => {
  if (sequenceSteps.value.length === 0) return true;
  return sequenceSteps.value.some(
    (step) => step.step_type === sequenceStepTypes.EMAIL,
  );
});

const hasLinkedInSteps = computed(() => {
  if (sequenceSteps.value.length === 0) return true;
  return sequenceSteps.value.some(
      (step) =>
          step.step_type && step.step_type.includes(sequenceStepTypes.LINKEDIN),
  );
});

const warningConfig = computed(() => ({
  emailConnectedKey: hasEmailSteps.value ? "email_connected" : null,
  linkedinConnectedKey: hasLinkedInSteps.value ? "linkedin_connected" : null,
  messages: {
    emailOnly: WARNING_MESSAGES.USER_SELECTION.EMAIL_ONLY_CONNECTED,
    linkedinOnly: WARNING_MESSAGES.USER_SELECTION.LINKEDIN_ONLY_CONNECTED,
    both: WARNING_MESSAGES.USER_SELECTION.BOTH_DISCONNECTED,
  },
}));

const isButtonDisabled = computed(() => {
  const hasSenderSelected =
    selectedMailboxes.value && selectedMailboxes.value.length > 0;
  return (
    !props.sequenceId ||
    isLoadingSequence.value ||
    isSaving.value ||
    !hasSenderSelected
  );
});

const mailboxTableHeader = computed(() => {
  if (currentActiveTab.value?.name === mailboxTabTypes.TEAMS) {
    return "Choose additional mailboxes from the selected team";
  } else {
    return "Choose additional mailboxes from the selected user(s)";
  }
});

const linkedInTableHeader = computed(() => {
  const selectedCount = selectedMailboxes.value.length;
  const integrationCount = linkedInIntegrations.value.length;
  const selectedIntegrationCount = linkedInSelection.value.selected_ids.length;
  
  return `LinkedIn Accounts (${selectedIntegrationCount}/${integrationCount} selected from ${selectedCount} user${selectedCount > 1 ? 's' : ''})`;
});

const ctaText = computed(() => {
  const entityCount = props.pendingValidationData?.entity_list?.length || 0;
  const validationType = props.pendingValidationData?.validation_type || "";

  return validationType === "LIST"
    ? "Add List"
    : entityCount > 0
      ? `Add ${entityCount} ${entityCount === 1 ? "Contact/Lead" : "Contacts/Leads"}`
      : "Add Contact/Lead";
});

const fetchSequenceDetails = async () => {
  if (!props.sequenceId) {
    return;
  }

  try {
    isLoadingSequence.value = true;
    sequenceDetails.value = null;

    const response = await SequenceService.fetchSequenceDetails(
      props.sequenceId,
    );

    if (response?.success && response?.sequence) {
      sequenceDetails.value = response.sequence;
    } else {
      console.error(
        "Failed to fetch sequence details:",
        response?.errors || response,
      );
      sequenceDetails.value = null;
    }
  } catch (error) {
    console.error("Error fetching sequence details:", error);
    sequenceDetails.value = null;
  } finally {
    isLoadingSequence.value = false;
  }
};

const fetchSequenceSteps = async () => {
  if (!props.sequenceId) {
    return;
  }

  try {
    isLoadingSteps.value = true;
    sequenceSteps.value = [];

    const response = await SequenceService.fetchSequenceSteps(props.sequenceId);

    if (response?.success && response?.steps) {
      sequenceSteps.value = response.steps;
    } else {
      console.error(
        "Failed to fetch sequence steps:",
        response?.errors || response,
      );
      sequenceSteps.value = [];
    }
  } catch (error) {
    console.error("Error fetching sequence steps:", error);
    sequenceSteps.value = [];
  } finally {
    isLoadingSteps.value = false;
  }
};

const fetchUsers = () =>
  fetchApiData(
    async () => {
      return await SequenceService.getUsersIntegrations();
    },
    "users",
    users,
    isLoadingUsers,
    (user) => ({
      label: user.email ? `${user.name} (${user.email})` : user.name,
      value: user.user_id,
      email_connected: !!user.email && !user.has_disconnected_email_account,
      linkedin_connected: user.has_linkedin_account,
      originalData: user,
    }),
  ).then(() => {
    autoSelectCurrentUser();
  });

const autoSelectCurrentUser = async () => {
  try {
    const currentUserId = localStorage.getItem("userId");

    if (!currentUserId || !users.value.length) {
      return;
    }

    const currentUser = users.value.find(
      (user) => user.value.toString() === currentUserId.toString(),
    );

    if (currentUser) {
      currentActiveTab.value = {
        index: 0,
        name: mailboxTabTypes.USERS,
      };

      selectedMailboxes.value = [currentUser.value];

      await fetchUserMailboxes([currentUser.value]);
      await fetchUserLinkedInIntegrations([currentUser.value]);
      showMailboxTable.value = true;
      showLinkedInTable.value = true;
    }
  } catch (error) {
    console.error("Error auto-selecting current user:", error);
  }
};

const fetchTeams = () =>
  fetchApiData(
    () => DashboardService.fetchTeams(),
    "teams",
    teams,
    isLoadingTeams,
    (team) => ({
      label: team.name,
      value: team.id,
    }),
  );

watch(
  [() => props.visible, () => props.sequenceId],
  async ([visible, sequenceId]) => {
    if (visible && sequenceId) {
      await nextTick();

      await Promise.all([
        fetchSequenceDetails(),
        fetchSequenceSteps(),
        fetchUsers(),
        fetchTeams(),
      ]);
    } else if (!visible) {
      sequenceDetails.value = null;
      isLoadingSequence.value = false;
      sequenceSteps.value = [];
      isLoadingSteps.value = false;
      selectedMailboxes.value = [];
      currentActiveTab.value = null;
      showMailboxTable.value = false;
      showLinkedInTable.value = false;
      userMailboxes.value = [];
      linkedInIntegrations.value = [];
      mailboxSelection.value = {
        selected_ids: [],
        unselected_ids: [],
        select_all: false,
      };
      linkedInSelection.value = {
        selected_ids: [],
        unselected_ids: [],
        select_all: false,
      };
    }
  },
  { immediate: true },
);

onMounted(async () => {
  if (props.visible && props.sequenceId) {
    await nextTick();
    await Promise.all([
      fetchSequenceDetails(),
      fetchSequenceSteps(),
      fetchUsers(),
      fetchTeams(),
    ]);
  }
});

const handleDropdownOpen = () => {
  isDropdownOpen.value = true;
};

const handleDropdownClose = () => {
  isDropdownOpen.value = false;
};

const handleClose = () => {
  emit("update:visible", false);
  sequenceDetails.value = null;
  isLoadingSequence.value = false;
  sequenceSteps.value = [];
  isLoadingSteps.value = false;
  isLoadingUsers.value = false;
  isLoadingTeams.value = false;
  selectedMailboxes.value = [];
  currentActiveTab.value = null;
  users.value = [];
  teams.value = [];
  showMailboxTable.value = false;
  showLinkedInTable.value = false;
  userMailboxes.value = [];
  linkedInIntegrations.value = [];
  mailboxSelection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };
  linkedInSelection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };
  isDropdownOpen.value = false;
};

const fetchUserMailboxes = async (userIds) => {
  if (!userIds?.length) return;

  return fetchApiData(
    () => DashboardService.fetchUserPersonalMailboxes(userIds),
    "mailboxes",
    userMailboxes,
    isLoadingUserMailboxes,
    (mailbox) => ({
      ...mailbox,
      email_account_id: mailbox.email_account_id || mailbox.id || 0,
      email: mailbox.email || 'No email',
      full_name: mailbox.full_name || '',
      vendor: mailbox.vendor || '',
      status: mailbox.status || MAILBOX_STATUS.DISCONNECTED,
      disabled: mailbox.status === MAILBOX_STATUS.DISCONNECTED,
    }),
  ).then(() => {
    mailboxSelection.value = {
      selected_ids: [],
      unselected_ids: [],
      select_all: false,
    };
  });
};

const fetchUserLinkedInIntegrations = async (userIds) => {
  if (!userIds?.length) return;

  return fetchApiData(
    async () => {
      const response = await SequenceService.getUsersIntegrations({
        user_ids: userIds,
        include_linkedin_details: true
      });
      
      // Extract LinkedIn integrations from the response (show all accounts)
      const allLinkedInIntegrations = [];
      if (response.success && response.users) {
        response.users.forEach(user => {
          if (user.linkedin_integrations) {
            allLinkedInIntegrations.push(...user.linkedin_integrations);
          }
        });
      }
      
      return {
        success: true,
        linkedin_integrations: allLinkedInIntegrations 
      };
    },
    "linkedin_integrations",
    linkedInIntegrations,
    isLoadingLinkedInIntegrations,
    (integration) => {
      const accountName = integration.account_name ||
        `${integration.user_name || 'Unknown User'} (${integration.user_email || 'No email'})`;
      
      return {
        linkedin_integration_id: Number(integration.linkedin_integration_id || integration.id || 0),
        user_id: Number(integration.user_id || 0),
        account_name: String(accountName),
        user_name: String(integration.user_name || 'Unknown User'),
        user_email: String(integration.user_email || ''),
        status: integration.status === 'CONNECTED' ? 'Connected' : 'Disconnected',
        disabled: integration.status !== 'CONNECTED',
      };
    },
  ).then(() => {
    linkedInSelection.value = {
      selected_ids: [],
      unselected_ids: [],
      select_all: false,
    };
  });
};

const fetchTeamMailboxes = async (teamIds = []) => {
  return fetchApiData(
    async () => {
      return await SequenceService.getTeamsPersonalMailboxes(teamIds);
    },
    "mailboxes",
    userMailboxes,
    isLoadingUserMailboxes,
    (mailbox) => ({
      ...mailbox,
      email_account_id: mailbox.email_account_id || mailbox.id || 0,
      email: mailbox.email || 'No email',
      full_name: mailbox.full_name || '',
      vendor: mailbox.vendor || '',
      status: mailbox.status || MAILBOX_STATUS.DISCONNECTED,
      disabled: mailbox.status === MAILBOX_STATUS.DISCONNECTED,
    }),
  ).then(() => {
    mailboxSelection.value = {
      selected_ids: [],
      unselected_ids: [],
      select_all: false,
    };
  });
};

const fetchTeamLinkedInIntegrations = async (teamIds = []) => {
  try {
    const teamResponse = await DashboardService.fetchTeamMembers(teamIds);

    if (teamResponse?.success) {
      const teamUsers = teamResponse.members || teamResponse.team_users || [];
      const teamUserIds = teamUsers.map(member => member.user_id || member.id);

      if (teamUserIds.length > 0) {
        await fetchUserLinkedInIntegrations(teamUserIds);
      }
    }
  } catch (error) {
    console.error("Error fetching team LinkedIn integrations:", error);
  }
};

const handleMailboxSelection = (selection) => {
  const connectedMailboxIds = userMailboxes.value
    .filter((mailbox) => mailbox.status === MAILBOX_STATUS.CONNECTED)
    .map((mailbox) => mailbox.email_account_id);

  const cleanedSelection = {
    ...selection,
    selected_ids: selection.selected_ids.filter((id) =>
      connectedMailboxIds.includes(id),
    ),
  };
  mailboxSelection.value = cleanedSelection;
};

const handleLinkedInSelection = (selection) => {
  const connectedIntegrationIds = linkedInIntegrations.value
    .filter((integration) => integration.status === 'Connected' && !integration.disabled)
    .map((integration) => integration.linkedin_integration_id);

  let cleanedSelection;
  if (selection.select_all) {
    const disconnectedIntegrationIds = linkedInIntegrations.value
      .filter((integration) => integration.status === 'Disconnected' || integration.disabled)
      .map((integration) => integration.linkedin_integration_id);
    
    cleanedSelection = {
      ...selection,
      unselected_ids: [...new Set([...selection.unselected_ids, ...disconnectedIntegrationIds])],
    };
  } else {
    cleanedSelection = {
      ...selection,
      selected_ids: selection.selected_ids.filter((id) =>
        connectedIntegrationIds.includes(id),
      ),
    };
  }
  
  linkedInSelection.value = cleanedSelection;
};

const getSelectedMailboxesArray = computed(() => selectedMailboxes.value || []);

const handleSave = async () => {
  if (!props.sequenceId) {
    console.error("Cannot save: No sequenceId provided");
    return;
  }

  try {
    isSaving.value = true;

    const selectedItems = getSelectedMailboxesArray.value;
    const configData = buildSequenceConfigData(
      selectedItems,
      currentActiveTab.value,
      {
        userMailboxes: userMailboxes.value,
        mailboxSelection: mailboxSelection.value,
        linkedInIntegrations: linkedInIntegrations.value,
        linkedInSelection: linkedInSelection.value,
        localMailboxRotation: false,
      },
    );

    const response = await SequenceService.saveSequenceEntityConfig(
      props.sequenceId,
      configData,
    );

    if (response.success) {
      emit("save", {
        configId: response.config_id,
        sequenceId: props.sequenceId,
        sequenceDetails: sequenceDetails.value,
        selectedItems,
      });
      handleClose();
    } else {
      console.error("Failed to save config:", response);
      message.error(
        `Failed to add people to ${featureNames.SEQUENCES}. Please try again.`,
      );
    }
  } catch (error) {
    console.error("Error handling save:", error);
    message.error("An error occurred while saving. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

const handleSelectionChange = async (data) => {
  selectedMailboxes.value = data.selectedValues;
  currentActiveTab.value = data.activeTab;

  if (
    data.activeTab?.name === mailboxTabTypes.USERS ||
    data.activeTab?.name === mailboxTabTypes.TEAMS
  ) {
    if (data.selectedValues.length > 0) {
      if (data.activeTab?.name === mailboxTabTypes.TEAMS) {
        await fetchTeamMailboxes(data.selectedValues);
        await fetchTeamLinkedInIntegrations(data.selectedValues);
      } else {
        await fetchUserMailboxes(data.selectedValues);
        await fetchUserLinkedInIntegrations(data.selectedValues);
      }
      showMailboxTable.value = true;
      showLinkedInTable.value = true;
    } else {
      showMailboxTable.value = false;
      showLinkedInTable.value = false;
      userMailboxes.value = [];
      linkedInIntegrations.value = [];
      mailboxSelection.value = {
        selected_ids: [],
        unselected_ids: [],
        select_all: false,
      };
      linkedInSelection.value = {
        selected_ids: [],
        unselected_ids: [],
        select_all: false,
      };
    }
  }
};

const handleTabChange = async (tabData) => {
  currentActiveTab.value = {
    index: tabData.tabIndex,
    name: tabData.tabName,
  };
  selectedMailboxes.value = [];
  showMailboxTable.value = false;
  showLinkedInTable.value = false;
  userMailboxes.value = [];
  linkedInIntegrations.value = [];
  mailboxSelection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };
  linkedInSelection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };

  if (tabData.tabName === mailboxTabTypes.USERS) {
    await autoSelectCurrentUser();
  }
};

const handleMailboxCollapseChange = (activeKeys) => {
  if (activeKeys.length > 0) {
    linkedInCollapseKey.value = [];
  }
};

const handleLinkedInCollapseChange = (activeKeys) => {
  if (activeKeys.length > 0) {
    mailboxCollapseKey.value = [];
  }
};
</script>

<style scoped>
:deep(.ant-collapse) {
  background: transparent;
  border: none;
}

:deep(.ant-collapse-header) {
  padding: 0 !important;
}

:deep(.ant-collapse-content-box) {
  padding: 16px 0 0 0 !important;
}

.mailbox-selector {
  margin-top: 16px;
}

.linkedin-selector {
  margin-top: 16px;
}
</style>
